import { inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { interval, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export const RELOAD_FN = new InjectionToken<() => void>('RELOAD_FN', {
  providedIn: 'root',
  factory: () => (): void => globalThis.location.reload(),
});

@Injectable({ providedIn: 'root' })
export class NewVersionCheckerService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly zone = inject(NgZone);
  private readonly reloadFn = inject(RELOAD_FN);
  private readonly destroy$ = new Subject<void>();
  
  private readonly VERSION_CHECK_INTERVAL_SECONDS = 60;

  public init(): void {
    this.watchForUpdates();
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.scheduleVersionCheck();
    this.handleVersionUpdates();
  }

  private scheduleVersionCheck(): void {
    this.zone.runOutsideAngular(() => {
      interval(this.VERSION_CHECK_INTERVAL_SECONDS * 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.swUpdate.checkForUpdate().catch((error: Error) => {
              console.error('Error checking for updates:', error);
            });
          }
        });
    });
  }

  private handleVersionUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((event: VersionEvent) => event.type === 'VERSION_READY'),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.zone.run(() => {
            this.reloadFn();
          });
        }
      });
  }
}